import { Eyebrow, H2, P, Section } from "@/components/SharedUI";

const PHASES = [
  {
    n: "01",
    name: "Discovery",
    duration: "2–4 weeks",
    summary:
      "We walk Country with your project — figuratively. Map the cultural authority, the governance gaps, the data sensitivities, the delivery risks.",
    deliverables: [
      "Readiness scorecard",
      "Governance gap analysis",
      "Cultural risk register",
      "Scope of engagement (written)",
    ],
    decisionGate: "Executive go/no-go before any further investment.",
  },
  {
    n: "02",
    name: "Architecture",
    duration: "4–8 weeks",
    summary:
      "We design the operating model: authority maps, decision-rights matrices, consent workflows, ICIP and IDS registers, the Lore-Gate controls.",
    deliverables: [
      "Authority map & decision-rights matrix",
      "ICIP & IDS registers",
      "Consent and FPIC workflows",
      "Q-Level escalation pathway",
      "Boardroom-ready architecture pack",
    ],
    decisionGate: "Architecture sign-off by your governance lead and cultural authority.",
  },
  {
    n: "03",
    name: "Protected Pilot",
    duration: "8–16 weeks",
    summary:
      "We run a controlled pilot — protected infrastructure, real workflows, fictional or community-approved data — to prove the architecture works in your operating environment.",
    deliverables: [
      "Controlled platform environment",
      "Live consent, ICIP and IDS workflows",
      "Audit evidence trail",
      "Pilot outcomes report",
      "Implementation plan for scale",
    ],
    decisionGate: "Pilot evaluation against the readiness scorecard — proceed, refine or hold.",
  },
  {
    n: "04",
    name: "Assurance",
    duration: "Ongoing",
    summary:
      "We support the system in production — assurance reporting, governance reviews, escalation handling, and renewal of cultural authority each cycle.",
    deliverables: [
      "Quarterly assurance reports",
      "Annual governance review",
      "Escalation triage and resolution",
      "Authority renewal cycle",
    ],
    decisionGate: "Annual licence renewal, with measurable governance outcomes.",
  },
];

export default function EngagementTimeline() {
  return (
    <section className="border-b border-stone-200 bg-stone-50">
      <Section>
        <div className="max-w-3xl mb-12">
          <Eyebrow>How we engage</Eyebrow>
          <H2 className="text-3xl md:text-4xl">
            A four-phase pathway with executive decision gates at every step.
          </H2>
          <P className="mt-6">
            No surprise scope, no surprise spend, no surprise cultural exposure. Each phase has a clear duration, a defined set of deliverables, and a formal go/no-go gate before the next dollar moves.
          </P>
        </div>

        {/* Desktop: horizontal connecting timeline. Mobile: vertical stack. */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-px bg-stone-200 border border-stone-200">
          {PHASES.map((p, i) => (
            <article
              key={p.n}
              data-testid={`engage-phase-${p.n}`}
              className="bg-[#FAFAF9] p-7 flex flex-col"
            >
              <div className="flex items-baseline justify-between gap-4 mb-5">
                <div className="font-display text-3xl tracking-tight text-stone-300 font-bold">
                  {p.n}
                </div>
                <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#9C4221]">
                  {p.duration}
                </div>
              </div>
              <h3 className="font-display text-2xl tracking-tight font-bold text-stone-900">
                {p.name}
              </h3>
              <p className="mt-3 text-sm text-stone-600 leading-relaxed">{p.summary}</p>

              <div className="mt-6">
                <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-stone-500 mb-3">
                  Deliverables
                </div>
                <ul className="space-y-2">
                  {p.deliverables.map((d) => (
                    <li key={d} className="flex gap-2 text-sm text-stone-700 leading-snug">
                      <span className="mt-2 h-1 w-1 bg-[#9C4221] rounded-full shrink-0" />
                      {d}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6 pt-5 border-t border-stone-200">
                <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-stone-500 mb-2">
                  Decision gate
                </div>
                <p className="text-xs text-stone-700 leading-relaxed">{p.decisionGate}</p>
              </div>

              {i < PHASES.length - 1 && (
                <div className="mt-6 text-[10px] tracking-[0.24em] uppercase font-bold text-stone-400 hidden lg:block">
                  → continues
                </div>
              )}
            </article>
          ))}
        </div>
      </Section>
    </section>
  );
}
